import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { enqueueMedia, enqueueNote, flush, newClientCaptureId, pendingFor } from '../state/capture-queue.ts';
import { magicQuoteApi, type MagicQuoteUploadFields } from '../api/endpoints.ts';
import { isLocalWalkthroughId, resolveWalkthroughId } from '../state/walkthrough-id.ts';

/**
 * The device side of a Magic Quote walkthrough: camera, recorder, and the
 * sync loop that gets what they produce to the server.
 *
 * Two things here are less obvious than they look.
 *
 * **Video is split on the device.** A Worker cannot decode video, and sending
 * a whole clip to a multimodal model would be both slow and expensive. But
 * the phone already has the decoded frames on screen, so while recording it
 * samples stills into a canvas and records the microphone into a second,
 * audio-only stream. The clip is uploaded whole for a person to watch; the
 * frames go to the vision pass and the audio to transcription. Sampling
 * belongs where the decoder already is.
 *
 * **Nothing waits for the network.** Every capture is queued to IndexedDB and
 * the UI updates immediately; the flush loop drains the queue whenever it can.
 * Pressing the shutter feels identical on five bars and on none — which is the
 * point, because the cleaner is talking to a customer, not watching a spinner.
 *
 * The children of a clip are tied to it by the *client's* id, not the
 * server's: at capture time the server id does not exist yet, and waiting for
 * it would put a network round trip back in the middle of the thing this hook
 * exists to keep off the network.
 */

/**
 * How many recent shots the viewfinder keeps a preview of.
 *
 * Six is about what fits along the bottom edge of a phone, and each one is a
 * decoded JPEG held in memory until it is revoked.
 */
const RECENT_PHOTO_COUNT = 6;

/** Stills sampled from a clip. Enough to characterize a room, few enough to be cheap. */
const FRAMES_PER_VIDEO = 3;

/** Longest clip the UI will record before stopping itself. */
export const MAX_VIDEO_SECONDS = 60;

/** Longest single voice note. */
export const MAX_AUDIO_SECONDS = 5 * 60;

/** Frame width sent for analysis; height follows the aspect ratio. */
const FRAME_WIDTH = 1024;

/**
 * How often a still is sampled out of a running clip.
 *
 * Short enough that an ordinary eight-second walkthrough clip yields two or
 * three, and `FRAMES_PER_VIDEO` is what stops a long one from yielding twenty.
 */
const FRAME_SAMPLE_INTERVAL_MS = 3000;

/**
 * `saving` is the gap between pressing Stop and the clip being on the phone.
 *
 * It exists because that gap is not instant and used to be invisible: the
 * recorders stop, then the clip, its sampled frames and its split audio are
 * each encoded and written to IndexedDB before anything on screen changes. The
 * elapsed timer even kept counting. Pressing Stop looked like it had done
 * nothing, so people pressed it again — on a recorder that was already
 * inactive, which really does nothing.
 */
export type CaptureMode = 'idle' | 'camera' | 'video' | 'audio' | 'saving';

/**
 * A photo this device took, previewed from its own bytes.
 *
 * Carries the id it was queued under so the screen can tell its local preview
 * apart from the server's copy of the same photo once that arrives — without
 * it the two are indistinguishable and the strip draws both, filling six slots
 * with three photos and hiding everything from earlier in the walk.
 */
export interface RecentPhoto {
  url: string;
  clientCaptureId: string;
}

export interface SyncState {
  /** Captures held locally, waiting for a network that will take them. */
  pending: number;
  /**
   * Of those, how many exist ONLY in this hook's memory.
   *
   * The store refused them — a private window, a full disk — so unlike every
   * other pending capture there is no copy on disk to come back to. Anything
   * that would unmount this hook has to know that: see the walkthrough
   * screen's redirect after a handover.
   */
  held: number;
  /**
   * How many of the current batch have already landed.
   *
   * Counted since the queue was last empty, which is what makes "3 of 14"
   * mean something: a bare pending count only ever goes down, and a cleaner
   * watching it drop from eleven has no idea whether that is nearly done or
   * barely started. Reset the moment the queue drains, so the next batch
   * starts from zero rather than inheriting the morning's total.
   */
  uploaded: number;
  /** Captures the server refused on their merits; no longer retried. */
  abandoned: number;
  /** True while a flush is in flight, for the "uploading" indicator. */
  syncing: boolean;
  online: boolean;
  /**
   * Why the queue is stuck, when it is.
   *
   * "3 waiting to upload" and nothing else is what a cleaner stares at while
   * deciding whether to trust the software, and it reads the same whether the
   * phone is in a basement or the server is refusing the file. The reason
   * turns a symptom into something somebody can act on — or report.
   */
  lastError: string | null;
}

export interface WalkthroughCapture {
  mode: CaptureMode;
  status: SyncState;
  /** Set when the camera or microphone could not be opened. */
  deviceError: string | null;
  /** Seconds elapsed in the current recording. */
  elapsed: number;
  /**
   * Which recorder ended because it hit its length limit rather than because
   * somebody pressed Stop, or null when the last stop was a person's.
   *
   * The two are indistinguishable on screen otherwise — the recorder simply
   * stops — and a cleaner mid-sentence reads that as the app breaking. Which
   * KIND matters as well as whether: a clip stops at a minute and a voice note
   * at five, so a notice that named the wrong one would be a false
   * explanation rather than a missing one. Cleared when the next recording
   * starts.
   */
  stoppedAtLimit: 'video' | 'audio' | null;
  /**
   * The last few photos, newest first, as local object URLs.
   *
   * From the blob that was just taken rather than from the server's copy of
   * it: the viewfinder covers the timeline, and a strip that waited for an
   * upload would be empty in exactly the place it is needed — a basement, a
   * driveway, anywhere the walkthrough is happening and the signal is not.
   */
  recentPhotos: readonly RecentPhoto[];
  videoRef: RefObject<HTMLVideoElement | null>;

  openCamera: () => Promise<void>;
  /** False when there was nothing to photograph — see the implementation. */
  takePhoto: () => Promise<boolean>;
  /** Photos the phone already has, re-encoded like the shutter's own. */
  addPhotos: (
    files: readonly File[],
    onProgress?: (progress: { done: number; total: number }) => void,
  ) => Promise<{ added: number; skipped: number }>;
  startVideo: () => Promise<void>;
  startAudio: () => Promise<void>;
  stopRecording: () => void;
  close: () => void;

  addNote: (text: string) => Promise<void>;
  markRoom: (name: string) => Promise<void>;
  /**
   * Uploads whatever is queued, now.
   *
   * `force` is a person asking rather than the timer: it ignores the backoff
   * a failed capture is sitting out, because a Retry button that waits out a
   * minute of exponential backoff looks broken and gets pressed again.
   *
   * Resolves to how many captures are still held in memory afterwards: the
   * ones the local store refused, which no queue will ever see and which go
   * with this screen when it unmounts.
   */
  sync: (options?: { force?: boolean }) => Promise<number>;
}

/**
 * A capture with nowhere durable to live: IndexedDB refused it and the direct
 * upload failed too, so memory is the only thing left holding it. Both kinds
 * go in one list because the order they were taken in is the order they must
 * reach the server — see the sequence discussion in `capture-queue.ts`.
 */
type HeldCapture =
  | { kind: 'media'; blob: Blob; upload: MagicQuoteUploadFields }
  | {
      kind: 'note';
      note: { type: 'text' | 'room_marker'; text: string; clientCaptureId: string; capturedAt: string };
    };

/** Whichever recording container this browser actually supports. */
function pickMimeType(candidates: readonly string[]): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

// Safari records MP4 and nothing else; Chrome and Firefox prefer WebM. Both
// families are accepted server-side, so the order here is simply "ask for what
// this device does natively".
const VIDEO_TYPES = ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm'];
const AUDIO_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];

/**
 * The one sentence this hook says for itself.
 *
 * Every other message here comes from the server or the browser; this one is
 * about a state only the hook can see — a capture that exists in memory and
 * nowhere else — and the screen renders it in the same warning slot as a
 * camera that would not open.
 */
const t = {
  notDurable:
    'This browser will not save captures on the device, so anything taken now is held in memory only — do not leave this screen until you have signal.',
};

export function useWalkthroughCapture(
  sessionId: string,
  options: { onCaptured?: () => void; allowAudio?: boolean; beforeSync?: () => Promise<void> } = {},
): WalkthroughCapture {
  const allowAudio = options.allowAudio !== false;
  const [mode, setMode] = useState<CaptureMode>('idle');
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [status, setStatus] = useState<SyncState>({
    pending: 0,
    held: 0,
    uploaded: 0,
    abandoned: 0,
    syncing: false,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    lastError: null,
  });

  const [recentPhotos, setRecentPhotos] = useState<readonly RecentPhoto[]>([]);
  const [stoppedAtLimit, setStoppedAtLimit] = useState<'video' | 'audio' | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioRecorderRef = useRef<MediaRecorder | null>(null);
  const frameTimerRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  /**
   * The "something arrived" callback, held in a ref rather than closed over.
   *
   * Callers pass an arrow function, so its identity changes on every render
   * of the page that owns this hook — and the walkthrough page re-renders
   * once a second to tick its elapsed timer. Depending on it directly made
   * `sync` new every second, which made the effect below tear down and
   * rebuild its eight-second interval every second: the timer never reached
   * eight, so an upload that failed while the browser stayed nominally
   * online was never retried on its own. The cleaner saw a pending capture
   * and a disabled Finish button until they pressed Retry by hand.
   *
   * The ref keeps the latest callback without making anything downstream
   * depend on its identity. Fixed here rather than asked of every caller,
   * because "memoize this or the retry loop silently stops" is not a
   * contract a hook should have.
   */
  const onCapturedRef = useRef(options.onCaptured);
  onCapturedRef.current = options.onCaptured;
  const onCaptured = useCallback(() => onCapturedRef.current?.(), []);
  const beforeSyncRef = useRef(options.beforeSync);
  beforeSyncRef.current = options.beforeSync;

  /**
   * The fallback's fallback: captures held in memory because IndexedDB would
   * not take them AND the direct upload failed.
   *
   * A browser with no usable IndexedDB (a private window, an embedded
   * webview, a full disk) has no durable queue, so the only thing between a
   * photo and oblivion is this array. It does not survive a reload — nothing
   * could — but it survives the walk to the next room with a signal, which is
   * the case that actually happens.
   */
  const heldRef = useRef<HeldCapture[]>([]);

  const refreshPending = useCallback(async () => {
    const queued = await pendingFor(sessionId);
    setStatus((current) => ({
      ...current,
      pending: queued.filter((item) => !item.abandoned).length + heldRef.current.length,
      held: heldRef.current.length,
      abandoned: queued.filter((item) => item.abandoned).length,
    }));
  }, [sessionId]);

  /** Uploads that have landed since the queue was last empty. */
  const uploadedRef = useRef(0);

  const sync = useCallback(
    async (options: { force?: boolean } = {}) => {
      // Disclosure, or any other gate, runs before a byte is uploaded. A
      // rejection (declined, or the notice could not be loaded) leaves the
      // queue where it is instead of posting into a 403 the flush would retry
      // forever.
      if (beforeSyncRef.current) {
        try {
          await beforeSyncRef.current();
        } catch {
          return heldRef.current.length;
        }
      }
      setStatus((current) => ({ ...current, syncing: true }));

      // The held captures go first and are removed one at a time, so a failure
      // partway through leaves the rest held rather than dropping them.
      //
      // Skipped entirely for a walkthrough the server has not been told about
      // yet: there is no walkthrough at the other end to upload to, and asking
      // anyway is a 404 that would be read as a refusal rather than an
      // absence. They keep until the handover re-keys them (see
      // `capture-queue.ts` → `reassignSession`).
      let uploadedHeld = 0;
      // Resolved rather than taken from the route: after a handover this
      // screen still says `local-…` for a few seconds, and these are captures
      // whose ONLY copy is this array — the store refused them. Testing the
      // route parameter meant they were never sent, and the redirect that
      // follows the handover unmounted the hook holding them.
      const target = resolveWalkthroughId(sessionId);
      while (!isLocalWalkthroughId(target) && heldRef.current.length > 0) {
        const next = heldRef.current[0]!;
        try {
          if (next.kind === 'media') await magicQuoteApi.uploadCapture(target, next.blob, next.upload);
          else await magicQuoteApi.addNote(target, next.note);
        } catch {
          break;
        }
        heldRef.current.shift();
        uploadedHeld += 1;
      }

      const result = await flush(sessionId, options);
      const stillPending = result.pending + heldRef.current.length;
      // The denominator resets when there is nothing left to send. Anything
      // else and a walkthrough's last capture would read as "40 of 41".
      uploadedRef.current = stillPending === 0 ? 0 : uploadedRef.current + result.uploaded + uploadedHeld;
      setStatus((current) => ({
        ...current,
        syncing: false,
        pending: stillPending,
        held: heldRef.current.length,
        uploaded: uploadedRef.current,
        abandoned: result.abandoned,
        lastError: result.lastError,
      }));
      if (result.uploaded + uploadedHeld > 0) onCaptured();
      return heldRef.current.length;
    },
    [sessionId, onCaptured],
  );

  // The sync loop runs on a timer AND on the online event: the event covers
  // walking out of the basement, and the timer covers a connection that came
  // back without the browser noticing — which, on a phone drifting between
  // one bar and none, is most of them.
  useEffect(() => {
    void refreshPending();
    const timer = window.setInterval(() => void sync(), 8000);
    const goOnline = () => {
      setStatus((current) => ({ ...current, online: true }));
      void sync();
    };
    const goOffline = () => setStatus((current) => ({ ...current, online: false }));
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [sync, refreshPending]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (frameTimerRef.current !== null) window.clearInterval(frameTimerRef.current);
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    frameTimerRef.current = null;
    tickRef.current = null;
    setElapsed(0);
  }, []);

  // Releasing the camera on unmount is not tidiness: a stream left open keeps
  // the phone's recording indicator lit in a customer's home.
  useEffect(() => stopStream, [stopStream]);

  /**
   * Ends a recording — the one way, whoever asked.
   *
   * The Stop button and the duration limit both come through here, and that is
   * the whole point of it existing. When the limit stopped the recorder on its
   * own it left the refs populated and the screen in `video`, so the Stop
   * button was still on screen over a recorder that had already finished:
   * pressing it fell through to the branch below, handed back the idle
   * controls and released the stream while `onstop` was still queueing the
   * clip. Start another capture in that window and the first recording's
   * cleanup, arriving late, stops the second one's camera.
   *
   * `saving` is therefore entered by both paths, and the refs are cleared by
   * both, so a second press has nothing left to act on.
   */
  const finishRecording = useCallback(
    (reason: 'user' | 'video' | 'audio' = 'user') => {
      setStoppedAtLimit(reason === 'user' ? null : reason);
      const recorder = recorderRef.current;
      const audioRecorder = audioRecorderRef.current;
      recorderRef.current = null;
      audioRecorderRef.current = null;

      // Said immediately, before any encoding: the tick stops, the buttons
      // change, and the cleaner can see that the press landed.
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
      tickRef.current = null;

      // Nothing to stop. Rather than leave the screen sitting on a Stop button
      // that cannot do anything — a recorder that failed to start, a second
      // press after the first — the capture screen is handed back.
      if (!recorder || recorder.state === 'inactive') {
        if (audioRecorder && audioRecorder.state !== 'inactive') audioRecorder.stop();
        stopStream();
        setMode('idle');
        return;
      }

      setMode('saving');
      recorder.stop();
      if (audioRecorder && audioRecorder.state !== 'inactive') audioRecorder.stop();
    },
    [stopStream],
  );

  // The preview URLs go with it. Held in a ref so the cleanup does not depend
  // on the list's identity and re-run on every photo.
  const photosRef = useRef<readonly RecentPhoto[]>([]);
  photosRef.current = recentPhotos;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url)), []);

  const openStream = useCallback(async (constraints: MediaStreamConstraints): Promise<MediaStream | null> => {
    setDeviceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (constraints.video && videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      return stream;
    } catch (error) {
      // Denied permission and absent hardware arrive the same way, and the
      // remedies differ, so the browser's own wording is shown rather than a
      // guess at which one happened.
      setDeviceError(error instanceof Error ? error.message : 'The camera could not be opened.');
      setMode('idle');
      return null;
    }
  }, []);

  /** Queues one media capture and returns the client id it was filed under. */
  /**
   * Writes one capture to the durable queue and, unless told otherwise, asks
   * for an upload pass.
   *
   * `defer` is for the callers that queue a BATCH — a stopped video puts the
   * clip, its sampled frames and its audio track in back to back, and a
   * library selection puts in as many photos as somebody chose. Each of those
   * asking for its own pass is a burst of flushes over one queue; one pass
   * after the last item is the same work done once.
   */
  const queue = useCallback(
    async (
      blob: Blob,
      fields: Omit<MagicQuoteUploadFields, 'clientCaptureId' | 'capturedAt'>,
      options: { defer?: boolean } = {},
    ): Promise<string> => {
      const clientCaptureId = newClientCaptureId();
      const upload: MagicQuoteUploadFields = {
        ...fields,
        clientCaptureId,
        // The device clock. A capture that spends ten minutes in the queue
        // still belongs where it was taken, not where it was uploaded.
        capturedAt: new Date().toISOString(),
      };

      const queued = await enqueueMedia(sessionId, blob, upload);
      if (!queued) {
        // IndexedDB is unavailable — a private window, an embedded browser,
        // a full disk. There is no durable safety net, so the capture goes
        // straight up; and if THAT fails it is held in memory and retried by
        // the sync loop rather than thrown into a `void` call nobody awaits.
        // The one outcome that must never happen is a photo the cleaner
        // believes was taken and that nobody ever sees.
        //
        // Unless there is nowhere to send it yet, in which case memory is the
        // only place it can go and the attempt would only produce a 404.
        if (isLocalWalkthroughId(resolveWalkthroughId(sessionId))) {
          heldRef.current.push({ kind: 'media', blob, upload });
          setStatus((current) => ({ ...current, pending: current.pending + 1, held: heldRef.current.length }));
          // Said out loud, because this capture is in memory and nowhere
          // else: the store refused it and the walkthrough has no server to
          // send it to yet. A reload, a back button or the phone discarding
          // the tab takes it — and the walkthrough would then list zero
          // captures with nothing to say about what happened to them.
          setDeviceError(t.notDurable);
          return clientCaptureId;
        }
        try {
          await magicQuoteApi.uploadCapture(resolveWalkthroughId(sessionId), blob, upload);
        } catch (error) {
          heldRef.current.push({ kind: 'media', blob, upload });
          setDeviceError(error instanceof Error ? error.message : 'That capture could not be uploaded.');
          setStatus((current) => ({ ...current, pending: current.pending + 1, held: heldRef.current.length }));
          return clientCaptureId;
        }
        onCaptured();
        return clientCaptureId;
      }

      await refreshPending();
      onCaptured();
      if (!options.defer) void sync();
      return clientCaptureId;
    },
    [sessionId, refreshPending, onCaptured, sync],
  );

  // ---------------------------------------------------------------------------
  // Photo
  // ---------------------------------------------------------------------------

  const openCamera = useCallback(async () => {
    setMode('camera');
    await openStream({ video: { facingMode: 'environment', width: { ideal: 1920 } }, audio: false });
  }, [openStream]);

  /**
   * Keeps a local preview of a photo just taken.
   *
   * Kept short and revoked on the way out: these are full-resolution JPEGs of
   * somebody's house, and a walkthrough is forty of them.
   */
  const rememberPhoto = useCallback((blob: Blob, clientCaptureId: string) => {
    setRecentPhotos((current) => {
      const next = [{ url: URL.createObjectURL(blob), clientCaptureId }, ...current];
      for (const stale of next.slice(RECENT_PHOTO_COUNT)) URL.revokeObjectURL(stale.url);
      return next.slice(0, RECENT_PHOTO_COUNT);
    });
  }, []);

  /**
   * One photo, and whether there actually is one.
   *
   * Both early returns are real: the shutter can be pressed in the moment
   * between the camera opening and the first frame arriving, and the canvas
   * encode can come back empty. Reporting success regardless is how a cleaner
   * ends up told "4 photos" while three exist — and walks out of the room.
   */
  const takePhoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return false;
    const blob = await grabFrame(video, video.videoWidth);
    if (!blob) return false;
    const clientCaptureId = await queue(blob, { type: 'photo' });
    rememberPhoto(blob, clientCaptureId);
    return true;
  }, [queue, rememberPhoto]);

  /**
   * Photos the phone already has, brought into the walkthrough.
   *
   * A cleaner who photographed the job before opening this app — or who was
   * sent pictures by the customer — had no way in, and the walk is worth just
   * as much either way.
   *
   * Re-encoded rather than uploaded as they came, for three reasons that all
   * bite on a phone. An iPhone's library is HEIC and the server accepts JPEG,
   * PNG and WebP, so a straight upload is a 415 the offline queue then gives
   * up on. A modern camera roll photo is twelve megapixels, which is minutes
   * of one-bar upload for detail the vision pass does not read. And a photo
   * taken in portrait carries its rotation in EXIF, which `createImageBitmap`
   * resolves here rather than leaving the model looking at a sideways kitchen.
   *
   * Returns what happened, because some of a selection can fail: a file that
   * will not decode is skipped and named rather than silently dropped.
   */
  /**
   * Photos chosen from the library, one at a time, with the phone given room
   * to breathe between them.
   *
   * Fifteen at once used to lose most of the selection. Each photo off a
   * modern phone is twelve megapixels, and decoding one costs about fifty
   * megabytes of bitmap plus a canvas to draw it into. Done in a tight loop
   * that is fifteen canvases outstanding before the first is collected, and
   * on iOS Safari — which caps total canvas memory per tab rather than
   * failing loudly — `drawImage` and `toBlob` simply start returning nothing.
   * Every photo after the ceiling was counted as "not an image", which is
   * both wrong and unactionable: they were images, the phone had just run out
   * of room to read them.
   *
   * So: one canvas for the whole batch instead of one per photo, a yield
   * between each so the browser can actually reclaim what was released, and
   * one retry after a yield for anything that still failed — which is usually
   * enough, because the failure is transient by nature. The batch also
   * reports progress, because fifteen photos is long enough on a phone that a
   * screen with no sign of movement reads as a screen that has hung.
   */
  const addPhotos = useCallback(
    async (
      files: readonly File[],
      onProgress?: (progress: { done: number; total: number }) => void,
    ): Promise<{ added: number; skipped: number }> => {
      let added = 0;
      let skipped = 0;

      const canvas = document.createElement('canvas');
      try {
        for (const [index, file] of files.entries()) {
          onProgress?.({ done: index, total: files.length });

          let blob = await reencodePhoto(file, canvas);
          if (!blob) {
            // A second go after the event loop has turned. The first failure
            // is far more often "no room right now" than "this is not a
            // photo", and the difference to the cleaner is whether half the
            // bathroom made it into the walkthrough.
            await breathe();
            blob = await reencodePhoto(file, canvas);
          }
          if (!blob) {
            skipped += 1;
            continue;
          }

          const clientCaptureId = await queue(blob, { type: 'photo' }, { defer: true });
          added += 1;
          rememberPhoto(blob, clientCaptureId);
          await breathe();
        }
      } finally {
        // Zeroed rather than merely dropped: this is what actually returns the
        // backing store on the browsers that account for it, and the reference
        // is about to go out of scope anyway.
        canvas.width = 0;
        canvas.height = 0;
      }
      onProgress?.({ done: files.length, total: files.length });

      // One pass for the whole selection rather than one per photo.
      if (added > 0) void sync();
      return { added, skipped };
    },
    [queue, sync, rememberPhoto],
  );

  // ---------------------------------------------------------------------------
  // Video — the clip, its sampled frames, and its audio track
  // ---------------------------------------------------------------------------

  const startVideo = useCallback(async () => {
    setStoppedAtLimit(null);
    setMode('video');
    // A company with audio switched off means it: the clip is recorded
    // without a microphone rather than recorded and then refused. Asking for
    // the track and dropping it later would still have put the customer's
    // voice on the device.
    const stream = await openStream({
      video: { facingMode: 'environment', width: { ideal: 1280 } },
      audio: allowAudio,
    });
    if (!stream) return;

    const videoType = pickMimeType(VIDEO_TYPES);
    const audioType = pickMimeType(AUDIO_TYPES);
    const clipChunks: Blob[] = [];
    const audioChunks: Blob[] = [];
    const frames: Blob[] = [];
    const startedAt = Date.now();

    const recorder = new MediaRecorder(stream, videoType ? { mimeType: videoType } : undefined);
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) clipChunks.push(event.data);
    };

    // The same microphone, recorded a second time on its own. Whisper takes
    // audio, not video, and a Worker has no way to demux one from the other.
    const audioTracks = stream.getAudioTracks();
    const audioRecorder =
      audioTracks.length > 0
        ? new MediaRecorder(new MediaStream(audioTracks), audioType ? { mimeType: audioType } : undefined)
        : null;
    if (audioRecorder) {
      audioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunks.push(event.data);
      };
    }

    /** Both recorders have to finish before the children can name their parent. */
    let videoDone = false;
    let audioDone = audioRecorder === null;
    /** Set once sampling is running, so stopping can take one last frame. */
    let stopSampler: (() => Promise<void>) | null = null;

    const finish = async () => {
      if (!videoDone || !audioDone) return;
      const seconds = Math.round((Date.now() - startedAt) / 1000);

      // One last still, so even a two-second clip carries something the
      // vision pass can read.
      if (frames.length === 0 && stopSampler) await stopSampler();

      const clip = new Blob(clipChunks, { type: videoType?.split(';')[0] ?? 'video/webm' });
      const parentId = await queue(clip, { type: 'video', durationSeconds: seconds }, { defer: true });

      // Children of the clip, tied to it by the client id the parent was
      // queued under. They upload independently and in any order; the server
      // resolves the link when each one lands.
      for (const frame of frames) {
        await queue(frame, { type: 'frame', derivedFromClientCaptureId: parentId }, { defer: true });
      }
      if (audioChunks.length > 0) {
        const audio = new Blob(audioChunks, { type: audioType?.split(';')[0] ?? 'audio/webm' });
        await queue(
          audio,
          { type: 'audio', durationSeconds: seconds, derivedFromClientCaptureId: parentId },
          { defer: true },
        );
      }

      // Everything from this clip is on the phone now; one pass sends it.
      void sync();
      stopStream();
      setMode('idle');
    };

    recorder.onstop = () => {
      videoDone = true;
      void finish();
    };
    if (audioRecorder) {
      audioRecorder.onstop = () => {
        audioDone = true;
        void finish();
      };
    }

    recorder.start();
    audioRecorder?.start();
    recorderRef.current = recorder;
    audioRecorderRef.current = audioRecorder;

    const sample = async () => {
      const video = videoRef.current;
      if (!video || frames.length >= FRAMES_PER_VIDEO) return;
      const frame = await grabFrame(video, FRAME_WIDTH);
      if (frame) frames.push(frame);
    };

    // Sampled every few seconds rather than spread across the maximum clip
    // length. Most walkthrough clips are eight seconds of one room, and a
    // schedule sized for a sixty-second clip would give those none at all —
    // which, since the pipeline never opens the video container itself, would
    // leave the clip contributing no visual evidence whatsoever.
    frameTimerRef.current = window.setInterval(() => void sample(), FRAME_SAMPLE_INTERVAL_MS);
    stopSampler = sample;

    tickRef.current = window.setInterval(() => {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      setElapsed(seconds);
      // Through the same door the Stop button uses, so the clip that ran to
      // the limit saves exactly like one the cleaner ended themselves.
      if (seconds >= MAX_VIDEO_SECONDS) finishRecording('video');
    }, 500);
  }, [allowAudio, openStream, queue, stopStream, finishRecording, sync]);

  // ---------------------------------------------------------------------------
  // Audio
  // ---------------------------------------------------------------------------

  const startAudio = useCallback(async () => {
    setStoppedAtLimit(null);
    setMode('audio');
    const stream = await openStream({ audio: true });
    if (!stream) return;

    const audioType = pickMimeType(AUDIO_TYPES);
    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(stream, audioType ? { mimeType: audioType } : undefined);
    const startedAt = Date.now();

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      void (async () => {
        const seconds = Math.round((Date.now() - startedAt) / 1000);
        const audio = new Blob(chunks, { type: audioType?.split(';')[0] ?? 'audio/webm' });
        await queue(audio, { type: 'audio', durationSeconds: seconds });
        stopStream();
        setMode('idle');
      })();
    };

    recorder.start();
    recorderRef.current = recorder;
    tickRef.current = window.setInterval(() => {
      const seconds = Math.round((Date.now() - startedAt) / 1000);
      setElapsed(seconds);
      if (seconds >= MAX_AUDIO_SECONDS) finishRecording('audio');
    }, 500);
  }, [openStream, queue, stopStream, finishRecording]);

  const stopRecording = useCallback(() => finishRecording('user'), [finishRecording]);

  const close = useCallback(() => {
    finishRecording();
    stopStream();
    setMode('idle');
  }, [finishRecording, stopStream]);

  // ---------------------------------------------------------------------------
  // Text
  // ---------------------------------------------------------------------------

  const enqueueText = useCallback(
    async (type: 'text' | 'room_marker', text: string) => {
      const clientCaptureId = newClientCaptureId();
      const capturedAt = new Date().toISOString();
      const queued = await enqueueNote(sessionId, { type, text }, clientCaptureId, capturedAt);
      if (!queued) {
        // Exactly the media path's bargain, which this used to get wrong: no
        // local store means no safety net, so the write goes straight up —
        // and if THAT fails the note is held in memory and retried by the
        // sync loop. Letting it reject instead lost the note outright, since
        // the submit handlers clear the draft and `void` the promise, so
        // nobody ever saw the failure and Finish went ahead without it. A
        // typed note is somebody's sentence about the property; it is not
        // recoverable from anywhere else.
        const note = { type, text, clientCaptureId, capturedAt };
        // As above: a walkthrough that exists only on this device has nothing
        // at the other end to write the note to.
        if (isLocalWalkthroughId(resolveWalkthroughId(sessionId))) {
          heldRef.current.push({ kind: 'note', note });
          setStatus((current) => ({ ...current, pending: current.pending + 1, held: heldRef.current.length }));
          setDeviceError(t.notDurable);
          return;
        }
        try {
          await magicQuoteApi.addNote(resolveWalkthroughId(sessionId), note);
        } catch (error) {
          heldRef.current.push({ kind: 'note', note });
          setDeviceError(error instanceof Error ? error.message : 'That note could not be uploaded.');
          setStatus((current) => ({ ...current, pending: current.pending + 1, held: heldRef.current.length }));
          return;
        }
        onCaptured();
        return;
      }
      await refreshPending();
      onCaptured();
      void sync();
    },
    [sessionId, refreshPending, onCaptured, sync],
  );

  const addNote = useCallback((text: string) => enqueueText('text', text), [enqueueText]);
  const markRoom = useCallback((name: string) => enqueueText('room_marker', name), [enqueueText]);

  return {
    mode,
    status,
    deviceError,
    elapsed,
    recentPhotos,
    stoppedAtLimit,
    videoRef,
    openCamera,
    takePhoto,
    addPhotos,
    startVideo,
    startAudio,
    stopRecording,
    close,
    addNote,
    markRoom,
    sync,
  };
}

/**
 * The widest a photo from the library is stored at.
 *
 * The same reasoning as the shutter's own JPEG quality: the vision pass reads
 * condition and clutter, not fine detail, and a twelve-megapixel original is
 * megabytes the phone has to push through whatever signal the house has.
 * Generous enough that a wide kitchen still resolves its counters.
 */
const LIBRARY_PHOTO_WIDTH = 1920;

/**
 * A photo from the device, normalized to the same JPEG the shutter produces.
 *
 * Null when the file is not an image this browser can decode — a video the
 * picker allowed through, a HEIC on a browser with no decoder for it, a
 * corrupt download. The caller counts those and says so rather than pretending
 * they were added.
 */
export async function reencodePhoto(file: File, canvas: HTMLCanvasElement): Promise<Blob | null> {
  if (!file.type.startsWith('image/')) return null;

  let bitmap: ImageBitmap;
  try {
    // `from-image` applies the EXIF rotation, so a photo taken in portrait is
    // stored the way up it was taken rather than the way the sensor read it.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }

  try {
    const scale = Math.min(1, LIBRARY_PHOTO_WIDTH / bitmap.width);
    // The caller's canvas, reused across the batch: resizing one is cheap,
    // and fifteen separate ones is what exhausts a phone. See `addPhotos`.
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    // Setting either dimension already clears it; this is for the case where
    // the previous photo had the same dimensions and it therefore did not.
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8));
  } finally {
    bitmap.close();
  }
}

/**
 * One turn of the event loop, so the browser can run the collection that
 * releases the bitmap and canvas memory the last photo used.
 *
 * A macrotask rather than a microtask on purpose: awaiting a resolved promise
 * yields to the microtask queue and lets nothing else happen, which is
 * exactly what a tight decode loop already was.
 */
function breathe(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** One still off a live video element, as a JPEG. */
async function grabFrame(video: HTMLVideoElement, width: number): Promise<Blob | null> {
  if (!video.videoWidth) return null;
  const scale = Math.min(1, width / video.videoWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => {
    // 0.8 is plenty for "is this shower dirty": the vision pass reads
    // condition and clutter, not fine detail, and every extra kilobyte is one
    // more the phone has to push through one bar of signal.
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
  });
}
